import { createTRPCReact } from "@trpc/react-query";
import { httpLink } from "@trpc/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import superjson from "superjson";
import type { AppRouter } from "../../api/router";
import type { ReactNode } from "react";

// ── API URL ──
// Production: Cloudflare Worker API endpoint (direct)
// Development: local Vite proxy
const API_URL = import.meta.env.VITE_API_URL || "https://parcleadpro.com/trpc";

export const trpc = createTRPCReact<AppRouter>();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on HTML responses (API not deployed)
        if (error?.message?.includes('<!DOCTYPE') || error?.message?.includes('<html')) {
          return false;
        }
        // Retry up to 2 times on network errors
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      // Gracefully handle API unavailability — show stale data rather than crash
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const trpcClient = trpc.createClient({
  links: [
    httpLink({
      url: API_URL,
      transformer: superjson,
      // httpOnly cookie-based auth — token sent automatically via cookie
      // No localStorage token needed (immune to XSS theft)
      headers() {
        return { "x-trpc-source": "react" };
      },
      // Graceful fetch wrapper — handles HTML responses and network errors
      // NOTE: We do NOT intercept 401 for auth.me here.
      // auth.me returning 401 for logged-out users is EXPECTED.
      // The useAuth hook handles this by checking meQuery.isError.
      // Intercepting 401 and returning synthetic responses breaks
      // tRPC + superjson deserialization and causes "undefined is not an object" crashes.
      async fetch(input, init) {
        const url = typeof input === 'string' ? input : (input as any).url;
        console.log(`[tRPC fetch] ${init?.method || 'GET'} ${url}`);
        try {
          const response = await globalThis.fetch(input, {
            ...(init ?? {}),
            credentials: "include", // Required for httpOnly cookie transmission
          });
          console.log(`[tRPC fetch] Response: ${response.status} ${response.statusText}`);

          // If the response is HTML (API not deployed / _redirects fallback),
          // return a synthetic JSON error so tRPC can handle it gracefully
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("text/html")) {
            console.log('[tRPC fetch] Got HTML response, API may be offline');
            return new Response(
              JSON.stringify({
                error: {
                  json: {
                    message: "API server is offline. Some features may be unavailable.",
                    code: -32004,
                    data: { code: "SERVICE_UNAVAILABLE", httpStatus: 503 },
                  },
                },
              }),
              { status: 200, headers: { "content-type": "application/json" } }
            );
          }

          return response;
        } catch (networkError: any) {
          console.error(`[tRPC fetch] Network error: ${networkError?.message || networkError}`);
          return new Response(
            JSON.stringify({
              error: {
                json: {
                  message: "Network error. Please check your connection.",
                  code: -32004,
                  data: { code: "NETWORK_ERROR", httpStatus: 503 },
                },
              },
            }),
            { status: 200, headers: { "content-type": "application/json" } }
          );
        }
      },
    }),
  ],
});

export function TRPCProvider({ children }: { children: ReactNode }) {
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </trpc.Provider>
  );
}
